import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Save, Copy, Plus, Search, ExternalLink, AlertTriangle, ClipboardPaste, X, CheckCircle2, Download, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface RateRow {
  id: string;
  quarter: string;
  jurisdiction_code: string;
  jurisdiction_name: string;
  country: 'US' | 'CA';
  is_ifta_jurisdiction: boolean;
  tax_rate: number | null;
  surcharge_rate: number | null;
}

const SURCHARGE_JURISDICTIONS = new Set(['IN', 'KY', 'VA']);

interface ParsedRow {
  code: string;
  tax_rate: number;
  surcharge_rate: number | null;
  matched: boolean;
}

function parsePaste(text: string, existingCodes: Set<string>): ParsedRow[] {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  const out: ParsedRow[] = [];
  for (const line of lines) {
    if (/^(jurisdiction|code|state|province|country)/i.test(line)) continue;
    const parts = line.split(/\t|,|;|\s{2,}/).map(p => p.trim()).filter(Boolean);
    if (parts.length < 2) continue;
    const code = parts[0].toUpperCase().replace(/[^A-Z]/g, '');
    if (code.length !== 2) continue;
    const tax = parseFloat(parts[1].replace(/[$,]/g, ''));
    if (isNaN(tax)) continue;
    const surRaw = parts[2] ? parseFloat(parts[2].replace(/[$,]/g, '')) : NaN;
    out.push({
      code,
      tax_rate: tax,
      surcharge_rate: isNaN(surRaw) ? null : surRaw,
      matched: existingCodes.has(code),
    });
  }
  return out;
}

function currentQuarter(d: Date = new Date()): string {
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `${d.getFullYear()}-Q${q}`;
}

function shiftQuarter(q: string, direction: -1 | 1): string {
  const [y, qStr] = q.split('-Q');
  let year = parseInt(y);
  let quarter = parseInt(qStr) + direction;
  if (quarter < 1) { quarter = 4; year -= 1; }
  if (quarter > 4) { quarter = 1; year += 1; }
  return `${year}-Q${quarter}`;
}

export function IFTARatesView() {
  const toast = useToast();
  const [quarter, setQuarter] = useState<string>(currentQuarter());
  const [rows, setRows] = useState<RateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [edits, setEdits] = useState<Record<string, { tax_rate: string; surcharge_rate: string }>>({});
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pastePreview, setPastePreview] = useState<ParsedRow[] | null>(null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => { loadRates(); }, [quarter]);

  async function loadRates() {
    try {
      setLoading(true);
      setEdits({});
      const { data, error } = await supabase
        .from('ifta_tax_rates')
        .select('*')
        .eq('quarter', quarter)
        .order('country')
        .order('jurisdiction_code');
      if (error) throw error;
      setRows((data || []) as RateRow[]);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load rates');
    } finally {
      setLoading(false);
    }
  }

  async function seedQuarter() {
    try {
      setSaving(true);
      // Pull the template from any prior quarter's rows (list of jurisdictions)
      const { data: template, error: tErr } = await supabase
        .from('ifta_tax_rates')
        .select('jurisdiction_code, jurisdiction_name, country, is_ifta_jurisdiction')
        .limit(100);
      if (tErr) throw tErr;
      if (!template || template.length === 0) {
        toast.error('No template jurisdictions found. Run the IFTA migration first.');
        return;
      }
      // De-duplicate by code
      const seen = new Set<string>();
      const jurisdictions = template.filter(j => {
        if (seen.has(j.jurisdiction_code)) return false;
        seen.add(j.jurisdiction_code);
        return true;
      });
      const toInsert = jurisdictions.map(j => ({ ...j, quarter }));
      const { error } = await supabase
        .from('ifta_tax_rates')
        .upsert(toInsert, { onConflict: 'quarter,jurisdiction_code', ignoreDuplicates: true });
      if (error) throw error;
      toast.success(`Seeded ${toInsert.length} jurisdictions for ${quarter}`);
      loadRates();
    } catch (err: any) {
      toast.error(err.message || 'Failed to seed');
    } finally {
      setSaving(false);
    }
  }

  async function copyFromPreviousQuarter() {
    try {
      setSaving(true);
      const prevQuarter = shiftQuarter(quarter, -1);
      const { data: prev, error: pErr } = await supabase
        .from('ifta_tax_rates')
        .select('jurisdiction_code, jurisdiction_name, country, is_ifta_jurisdiction, tax_rate, surcharge_rate')
        .eq('quarter', prevQuarter);
      if (pErr) throw pErr;
      if (!prev || prev.length === 0) {
        toast.error(`No rates found in ${prevQuarter}`);
        return;
      }
      const toUpsert = prev.map(r => ({ ...r, quarter }));
      const { error } = await supabase
        .from('ifta_tax_rates')
        .upsert(toUpsert, { onConflict: 'quarter,jurisdiction_code' });
      if (error) throw error;
      toast.success(`Copied ${toUpsert.length} rates from ${prevQuarter}`);
      loadRates();
    } catch (err: any) {
      toast.error(err.message || 'Failed to copy');
    } finally {
      setSaving(false);
    }
  }

  async function fetchFromSource() {
    const [yStr, qStr] = quarter.split('-Q');
    const year = parseInt(yStr, 10);
    const q = parseInt(qStr, 10);
    if (!year || !q) {
      toast.error('Invalid quarter');
      return;
    }
    try {
      setFetching(true);
      const { data, error } = await supabase.functions.invoke('fetch-ifta-rates', {
        body: { year, quarter: q },
      });
      if (error) throw new Error(error.message || 'Fetch failed');
      if (!data?.success) {
        if (data?.not_published) {
          toast.info?.(data.message || `Rates for ${quarter} haven't been published yet.`);
          return;
        }
        throw new Error(data?.error || 'Source returned no rows for this quarter');
      }
      if (!Array.isArray(data.rows) || data.rows.length === 0) {
        throw new Error('Source returned no rows for this quarter');
      }
      const existingCodes = new Set(rows.map(r => r.jurisdiction_code));
      const preview: ParsedRow[] = (data.rows as Array<{
        code: string;
        tax_rate: number;
        surcharge_rate: number;
      }>).map(r => ({
        code: r.code,
        tax_rate: r.tax_rate,
        surcharge_rate: SURCHARGE_JURISDICTIONS.has(r.code)
          ? r.surcharge_rate
          : null,
        matched: existingCodes.has(r.code),
      }));
      setPastePreview(preview);
      setPasteText('');
      setShowPaste(true);
      if (data.quarter_mismatch) {
        toast.info?.(
          `Source page returned ${data.returned_quarter} instead of ${quarter} — review before applying.`
        );
      } else {
        toast.success(`Fetched ${preview.length} rates from ifta-calculator.com`);
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to fetch rates');
    } finally {
      setFetching(false);
    }
  }

  async function pasteFromClipboard() {
    try {
      const text = await navigator.clipboard.readText();
      setPasteText(text);
      setPastePreview(parsePaste(text, new Set(rows.map(r => r.jurisdiction_code))));
    } catch {
      toast.error('Clipboard unavailable — paste into the box manually');
    }
  }

  async function applyPaste() {
    if (!pastePreview) return;
    const valid = pastePreview.filter(p => p.matched);
    if (valid.length === 0) {
      toast.error('No matching jurisdictions found');
      return;
    }
    try {
      setSaving(true);
      const byCode = new Map(rows.map(r => [r.jurisdiction_code, r]));
      for (const p of valid) {
        const row = byCode.get(p.code);
        if (!row) continue;
        const payload: Record<string, unknown> = {
          tax_rate: p.tax_rate,
          updated_at: new Date().toISOString(),
        };
        if (SURCHARGE_JURISDICTIONS.has(p.code)) {
          payload.surcharge_rate = p.surcharge_rate;
        }
        const { error } = await supabase
          .from('ifta_tax_rates')
          .update(payload)
          .eq('id', row.id);
        if (error) throw error;
      }
      toast.success(`Imported ${valid.length} rate${valid.length !== 1 ? 's' : ''}`);
      setShowPaste(false);
      setPasteText('');
      setPastePreview(null);
      loadRates();
    } catch (err: any) {
      toast.error(err.message || 'Failed to import');
    } finally {
      setSaving(false);
    }
  }

  function editRow(id: string, field: 'tax_rate' | 'surcharge_rate', value: string) {
    setEdits(prev => {
      const current = prev[id] || {
        tax_rate: rows.find(r => r.id === id)?.tax_rate?.toString() ?? '',
        surcharge_rate: rows.find(r => r.id === id)?.surcharge_rate?.toString() ?? '',
      };
      return { ...prev, [id]: { ...current, [field]: value } };
    });
  }

  async function saveAll() {
    const ids = Object.keys(edits);
    if (ids.length === 0) {
      toast.info?.('No changes to save');
      return;
    }
    try {
      setSaving(true);
      const updates = ids.map(id => {
        const e = edits[id];
        return {
          id,
          tax_rate: e.tax_rate === '' ? null : parseFloat(e.tax_rate),
          surcharge_rate: e.surcharge_rate === '' ? null : parseFloat(e.surcharge_rate),
          updated_at: new Date().toISOString(),
        };
      });
      for (const u of updates) {
        const { error } = await supabase
          .from('ifta_tax_rates')
          .update({ tax_rate: u.tax_rate, surcharge_rate: u.surcharge_rate, updated_at: u.updated_at })
          .eq('id', u.id);
        if (error) throw error;
      }
      toast.success(`Saved ${updates.length} rate${updates.length !== 1 ? 's' : ''}`);
      loadRates();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      r.jurisdiction_code.toLowerCase().includes(q) ||
      r.jurisdiction_name.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const missingRateCount = rows.filter(r => r.is_ifta_jurisdiction && r.tax_rate == null).length;
  const dirty = Object.keys(edits).length;

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
      <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4 border-b border-slate-200">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900">IFTA Tax Rates</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Source:{' '}
              <a
                href="https://www.iftach.org/taxmatrix4/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline inline-flex items-center gap-0.5"
              >
                iftach.org tax matrix <ExternalLink className="h-3 w-3" />
              </a>{' '}
              — published quarterly. Enter rates as USD/gallon.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setQuarter(shiftQuarter(quarter, -1))}
              className="p-2 rounded-lg border border-slate-300 hover:bg-slate-50"
              title="Previous quarter"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm font-semibold text-slate-700 min-w-[90px] text-center">
              {quarter}
            </div>
            <button
              onClick={() => setQuarter(shiftQuarter(quarter, 1))}
              className="p-2 rounded-lg border border-slate-300 hover:bg-slate-50"
              title="Next quarter"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3 border-b border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-2">
          {rows.length === 0 && !loading && (
            <button
              onClick={seedQuarter}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" /> Seed jurisdictions
            </button>
          )}
          {rows.length > 0 && (
            <>
              <button
                onClick={fetchFromSource}
                disabled={saving || fetching}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-50"
                title={`Auto-fetch ${quarter} rates from ifta-calculator.com`}
              >
                {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {fetching ? 'Fetching…' : `Fetch ${quarter} rates`}
              </button>
              <button
                onClick={copyFromPreviousQuarter}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                title={`Copy rates from ${shiftQuarter(quarter, -1)}`}
              >
                <Copy className="h-4 w-4" /> Copy from {shiftQuarter(quarter, -1)}
              </button>
            </>
          )}
          {missingRateCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-700 text-xs">
              <AlertTriangle className="h-3 w-3" />
              {missingRateCount} IFTA jurisdiction{missingRateCount !== 1 ? 's' : ''} missing rate
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search jurisdictions..."
              className="pl-9 pr-3 py-1.5 rounded-lg border border-slate-300 text-sm"
            />
          </div>
          <button
            onClick={saveAll}
            disabled={saving || dirty === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            Save{dirty > 0 ? ` (${dirty})` : ''}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Code</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Jurisdiction</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Country</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
              <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 uppercase">Tax Rate ($/gal)</th>
              <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 uppercase">Surcharge ($/gal)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                  No rates configured for {quarter}. Click <strong>Seed jurisdictions</strong> above to start.
                </td>
              </tr>
            )}
            {!loading && filteredRows.map(r => {
              const edit = edits[r.id];
              const taxVal = edit?.tax_rate ?? (r.tax_rate?.toString() ?? '');
              const surVal = edit?.surcharge_rate ?? (r.surcharge_rate?.toString() ?? '');
              const hasSurcharge = SURCHARGE_JURISDICTIONS.has(r.jurisdiction_code);
              const edited = !!edit;
              return (
                <tr key={r.id} className={`hover:bg-slate-50 ${edited ? 'bg-amber-50/50' : ''}`}>
                  <td className="px-4 py-2 text-sm font-mono font-semibold text-slate-800">{r.jurisdiction_code}</td>
                  <td className="px-4 py-2 text-sm text-slate-700">{r.jurisdiction_name}</td>
                  <td className="px-4 py-2 text-sm text-slate-600">{r.country}</td>
                  <td className="px-4 py-2 text-sm">
                    {r.is_ifta_jurisdiction ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">IFTA</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-slate-200 text-slate-600">Non-IFTA</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      value={taxVal}
                      disabled={!r.is_ifta_jurisdiction}
                      onChange={e => editRow(r.id, 'tax_rate', e.target.value)}
                      placeholder={r.is_ifta_jurisdiction ? '0.0000' : 'n/a'}
                      className="w-28 px-2 py-1 rounded border border-slate-300 text-sm text-right disabled:bg-slate-100 disabled:text-slate-400"
                    />
                  </td>
                  <td className="px-4 py-2 text-right">
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      value={surVal}
                      disabled={!hasSurcharge}
                      onChange={e => editRow(r.id, 'surcharge_rate', e.target.value)}
                      placeholder={hasSurcharge ? '0.0000' : '—'}
                      className="w-28 px-2 py-1 rounded border border-slate-300 text-sm text-right disabled:bg-slate-100 disabled:text-slate-400"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {dirty > 0 && (
        <div className="px-6 py-3 border-t border-slate-200 bg-amber-50 text-sm text-amber-800 flex items-center justify-between">
          <span>{dirty} unsaved change{dirty !== 1 ? 's' : ''}</span>
          <button
            onClick={saveAll}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> Save all
          </button>
        </div>
      )}

      {showPaste && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Review rates for {quarter}</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Fetched from{' '}
                  <a href="https://ifta-calculator.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5">
                    ifta-calculator.com <ExternalLink className="h-3 w-3" />
                  </a>. Check the values before applying.
                </p>
              </div>
              <button
                onClick={() => { setShowPaste(false); setPasteText(''); setPastePreview(null); }}
                className="p-2 rounded-lg hover:bg-slate-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="px-6 py-4 flex-1 overflow-y-auto">
              {!pastePreview ? (
                <>
                  <div className="mb-3 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600 font-mono whitespace-pre">
{`AL  0.2900
CA  0.9010
IN  0.5700  0.1100   ← surcharge for IN, KY, VA only
KY  0.2160  0.0440
VA  0.2620  0.0350`}
                  </div>
                  <textarea
                    value={pasteText}
                    onChange={e => setPasteText(e.target.value)}
                    placeholder="Paste rows here — tab, comma, or multi-space separated..."
                    className="w-full h-64 p-3 rounded-lg border border-slate-300 text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </>
              ) : (
                <div>
                  <div className="flex items-center gap-2 mb-3 text-sm">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700">
                      <CheckCircle2 className="h-3 w-3" /> {pastePreview.filter(p => p.matched).length} will update
                    </span>
                    {pastePreview.some(p => !p.matched) && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-700">
                        <AlertTriangle className="h-3 w-3" /> {pastePreview.filter(p => !p.matched).length} unknown code{pastePreview.filter(p => !p.matched).length !== 1 ? 's' : ''} (skipped)
                      </span>
                    )}
                  </div>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Code</th>
                          <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600 uppercase">Tax Rate</th>
                          <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600 uppercase">Surcharge</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {pastePreview.map((p, i) => (
                          <tr key={i} className={p.matched ? '' : 'bg-amber-50/50 text-slate-400'}>
                            <td className="px-3 py-1.5 font-mono font-semibold">{p.code}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{p.tax_rate.toFixed(4)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">
                              {SURCHARGE_JURISDICTIONS.has(p.code) && p.surcharge_rate != null
                                ? p.surcharge_rate.toFixed(4)
                                : '—'}
                            </td>
                            <td className="px-3 py-1.5 text-xs">
                              {p.matched
                                ? <span className="text-emerald-600">✓ ready</span>
                                : <span className="text-amber-600">unknown</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
              <button
                onClick={pasteFromClipboard}
                className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
              >
                <ClipboardPaste className="h-4 w-4" /> Paste from clipboard
              </button>
              <div className="flex items-center gap-2">
                {!pastePreview ? (
                  <button
                    onClick={() => setPastePreview(parsePaste(pasteText, new Set(rows.map(r => r.jurisdiction_code))))}
                    disabled={!pasteText.trim()}
                    className="px-4 py-1.5 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    Preview
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => setPastePreview(null)}
                      className="px-4 py-1.5 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={applyPaste}
                      disabled={saving || pastePreview.filter(p => p.matched).length === 0}
                      className="px-4 py-1.5 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-50"
                    >
                      Apply {pastePreview.filter(p => p.matched).length} rate{pastePreview.filter(p => p.matched).length !== 1 ? 's' : ''}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
